/**
 * Табелі обліку робочого часу — список (канон Норми #29, 2026-08-23):
 * єдиний серверний реєстр місячних + денних (/timesheets/registry),
 * InfiniteDocGrid, фільтри орг/тип/період рядком, колонки «Вкладення»/«Підпис».
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { timesheets, type TimesheetRegistryRow } from '@/api/timesheets';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import PeriodFilter, { useHrPeriodFilter, MONTHS_UK } from '@/shared/components/PeriodFilter';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';

type Kind = '' | 'monthly' | 'daily';

export default function TimesheetsPage() {
  const navigate = useNavigate();
  const [kind, setKind] = useState<Kind>('');
  const [orgId, setOrgId] = useOrgFilter();
  // Період: поля сторінки порожні → період шапки; заповнені → лише вони (ч.775).
  const per = useHrPeriodFilter();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const { datasource, total } = useInfiniteGridDatasource<TimesheetRegistryRow>(
    (limit, offset, sort) => timesheets.registry({
      organization_id: orgId || undefined,
      kind: kind || undefined,
      ...per.params,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, kind, ...per.key],
    { attachmentsType: 'doc.timesheets' },
  );
  // Контроль підписання — для місячних табелів (doc.timesheets).
  const sc = useSignControlInfinite<TimesheetRegistryRow>('doc.timesheets', orgId);

  const cols = useMemo<ColDef<TimesheetRegistryRow>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: TimesheetRegistryRow }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    {
      field: 'kind', headerName: 'Тип', width: 100,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: TimesheetRegistryRow }) => !p.data ? '' : p.data.kind === 'daily'
        ? <span style={{ color: '#2563eb' }}>Денний</span>
        : <span style={{ color: 'var(--badge-green-fg)' }}>Місячний</span>,
    },
    { field: 'document_number', headerName: '№', width: 140 },
    {
      colId: 'period', field: 'period_date', headerName: 'Період / Дата', width: 160,
      valueFormatter: (p) => {
        if (!p.data) return '';
        const d = new Date(p.data.period_date);
        return p.data.kind === 'daily'
          ? d.toLocaleDateString('uk-UA')
          : `${MONTHS_UK[d.getMonth() + 1]} ${d.getFullYear()}`;
      },
    },
    {
      field: 'organization_id', headerName: 'Організація', flex: 1, minWidth: 200,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 110,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters" style={{ alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Табелі обліку робочого часу<TitleTotal total={total} /></h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="primary" onClick={() => navigate('/timesheets/new')}>
            + Місячний
          </button>
          <button className="primary" onClick={() => navigate('/daily-timesheets/new')}>
            + Денний
          </button>
        </div>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value as Kind)} style={{ width: 130 }}>
          <option value="">усі табелі</option>
          <option value="monthly">Місячні</option>
          <option value="daily">Денні</option>
        </select>
        <PeriodFilter {...per.bind} />
      </div>
      <InfiniteDocGrid<TimesheetRegistryRow>
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="timesheets-registry"
        onRowDoubleClicked={(r) => navigate(r.kind === 'daily' ? `/daily-timesheets/${r.id}` : `/timesheets/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
